import { Link } from 'react-router-dom';
import { topicsFor, buildLearnPrompt } from '@/learn';
import type { LearnChain, LearnContext } from '@/learn';
import { Button } from '@/components/ui/Button';

export type AiStatus = 'idle' | 'streaming' | 'done' | 'error';

export interface ExplainPanelProps {
  ctx: LearnContext;
  chain: LearnChain;
  onAskAi: (prompt: string) => void;
  aiReply: string | null;
  aiStatus: AiStatus;
  aiError: string | null;
}

/**
 * Pure, props-driven: renders the Learn topics that apply to this position, filled in with its
 * own numbers, plus a button to ask the AI to expand on them. Owns no state — `ExplainDialog`
 * owns the AI streaming state and passes it down.
 */
export function ExplainPanel({ ctx, chain, onAskAi, aiReply, aiStatus, aiError }: ExplainPanelProps) {
  const topics = topicsFor(ctx, chain);

  return (
    <div className="space-y-6">
      <Link
        data-testid="basics-link"
        to="/learn#what-is-an-option"
        className="block text-xs text-brand-600 hover:underline"
      >
        New to this? Start with the basics.
      </Link>

      {topics.map((topic) => (
        <section key={topic.id} data-testid={`topic-${topic.id}`} className="space-y-2">
          <h3 data-testid="topic-title" className="text-sm font-semibold text-gray-900">
            {topic.title}
          </h3>
          {topic.explain(ctx).map((line, i) => (
            <p key={i} data-testid="explain-line" className="text-sm text-gray-600">
              {line}
            </p>
          ))}
          <Link to={`/learn#${topic.id}`} className="text-xs text-brand-600 hover:underline">
            Read more
          </Link>
        </section>
      ))}

      <div className="space-y-3 border-t border-gray-100 pt-4">
        <Button
          data-testid="ask-ai"
          size="sm"
          disabled={aiStatus === 'streaming'}
          onClick={() => onAskAi(buildLearnPrompt(ctx, chain))}
        >
          {aiStatus === 'streaming' ? 'Thinking…' : 'Ask AI to expand'}
        </Button>
        {aiReply != null && (
          <div data-testid="ai-reply" className="whitespace-pre-wrap text-sm text-gray-900">
            {aiReply}
          </div>
        )}
        {aiStatus === 'error' && aiError && (
          <p data-testid="ai-error" className="text-sm text-red-600">
            {aiError}
          </p>
        )}
      </div>
    </div>
  );
}
